/* ADG Product (Individual Art Page) — variant picker + gallery + in-place
   product-type swap (Section Rendering API + History API). No deps.

   Swap is progressive enhancement: the "Select a Product" dropdown swaps the
   whole buy box in place without a reload; on any failure it falls back to a
   normal navigation. Every OTHER link on the site navigates normally. */
(function () {
  'use strict';

  var WRAP_SEL = '[data-adg-pdp-root]';
  var SECTION_ID = 'main';   // section key in templates/product.json

  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function getWrap() { return document.querySelector(WRAP_SEL); }

  // ---- Per-product init (re-run after every swap) ----
  function initPdp(wrap) {
    if (!wrap) return;
    var pdp = wrap.querySelector('[data-adg-pdp]');
    if (!pdp) return;

    var variants = [];
    var vjson = wrap.querySelector('[data-adg-variants]');
    if (vjson) { try { variants = JSON.parse(vjson.textContent); } catch (e) {} }

    var optionSelects = Array.prototype.slice.call(pdp.querySelectorAll('[data-adg-option]'))
      .sort(function (a, b) { return (+a.dataset.adgOption) - (+b.dataset.adgOption); });
    var idInput = pdp.querySelector('[data-adg-variant-id]');
    var priceEl = pdp.querySelector('[data-adg-price]');
    var addBtn = pdp.querySelector('[data-adg-add]');
    var addLabel = pdp.querySelector('[data-adg-add-label]');
    var mainImg = pdp.querySelector('[data-adg-main-img]');
    var frameSelect = pdp.querySelector('select[name="options[Frame]"]');
    var colorField = pdp.querySelector('[data-adg-color-field]');
    var colorSelect = colorField ? colorField.querySelector('select') : null;

    /* ---- Gallery -------------------------------------------------------
       The strip renders EVERY image; which ones are visible is decided here.

       An image is either a VARIANT image (some variant points at it) or a
       plain product image. Plain images always show — for an ADG art product
       that is the flat artwork, and for anything added normally through the
       Shopify admin it is every image the product has, so those products keep
       behaving exactly as they did. Variant images show only when their
       variant is the one selected.

       Before the shopper has chosen, there is no variant to match, so a
       capped sample of variant images stands in — that is the "show me the
       range" view, and it is what the page did before this change. */
    var thumbsWrap = pdp.querySelector('[data-adg-thumbs]');
    var moreBtn = pdp.querySelector('[data-adg-thumbs-more]');
    var thumbs = Array.prototype.slice.call(pdp.querySelectorAll('[data-adg-thumb]'));

    // Each option's real values, straight off its dropdown — the only
    // vocabulary the alt fallback below is allowed to recognise.
    var optionNames = optionSelects.map(function (s) {
      return Array.prototype.map.call(s.options, function (o) { return o.value; })
        .filter(function (v) { return !!v; });
    });

    var variantImageIds = {};      // image id -> true (belongs to SOME variant)
    variants.forEach(function (v) {
      if (v.featured_image && v.featured_image.id != null) variantImageIds[String(v.featured_image.id)] = true;
    });

    /* THE SAME RULE, FOR PRODUCTS SHOPIFY CAN'T ANSWER FOR.

       Only art prints carry variant.featured_image. Canvases, metal signs and
       postcards have none at all, so every image read as static and nothing
       narrowed — the page worked on one format and looked broken on the rest.

       The link does exist on those products, just in the alt text the artwork
       pipeline writes:

         "…- Metal Sign / 9x12 / Env"          -> Size 9x12
         "…- 11x14 / Simple / Black / Env"     -> Size 11x14, Frame Simple, …
         "…- Art Print - Artwork"              -> no option values: static

       So when a product has no variant images, read the constraints off the
       alt instead. The vocabulary is the product's OWN option values, never a
       hardcoded list of sizes — a token counts only if it matches a value
       that product actually offers, which is why "Metal Sign" and "Env" fall
       through as noise and the flat artwork stays static.

       Values are matched on their first word, because the pipeline writes
       "8x10" where the option reads "8x10 inch". */
    function altConstraints(alt) {
      if (!alt || !optionSelects.length) return null;
      var tokens = [];
      alt.split('/').forEach(function (part) {
        part.split(' - ').forEach(function (bit) {
          var t = bit.trim().toLowerCase();
          if (t) tokens.push(t);
        });
      });
      var found = {}, any = false;
      optionNames.forEach(function (values, i) {
        for (var vi = 0; vi < values.length; vi++) {
          var val = values[vi];
          var full = val.toLowerCase();
          var head = full.split(' ')[0];
          if (tokens.indexOf(full) > -1 || tokens.indexOf(head) > -1) {
            found[i] = val; any = true; return;
          }
        }
      });
      if (!any) return null;                 // no option values named: static
      // The pipeline omits an axis when its value is "None" — an unframed
      // mockup is "8x10 / Env", not "8x10 / None / None". So an unnamed axis
      // means None on any product that actually offers None.
      optionNames.forEach(function (values, i) {
        if (found[i] === undefined && values.indexOf('None') > -1) found[i] = 'None';
      });
      return found;
    }

    // ALWAYS computed. This used to be a fallback consulted only when NO
    // variant carried a featured image — which broke the moment a product had
    // SOME: the puzzle's two size variants each claim one angle shot, so the
    // other seven images read as static and the strip never narrowed
    // (Justin, 2026-08-12). Id-claims and alt-claims now coexist; an image
    // must satisfy whichever claims apply to it.
    var altMap = {};               // image id -> {optionIndex: value}
    thumbs.forEach(function (t) {
      var c = altConstraints(t.dataset.imgAlt);
      if (c) altMap[t.dataset.imgId] = c;
    });
    function altMatches(c, vals) {
      for (var i in c) {
        if (vals[i] && vals[i] !== c[i]) return false;
      }
      return true;
    }

    /* Which images match what the shopper has chosen SO FAR — not what the
       fully-resolved variant is. Choose 8x10 and you get every 8x10 image:
       unframed, Simple/Black, Standard/Gray and the rest. Choose a frame too
       and it narrows again.

       Filtering straight to the single resolved variant would be honest but
       bleak — two images on a page selling a print in six sizes and four
       frames. This keeps the page truthful (an 18x24 never shows while 8x10
       is selected) without emptying it, and seeing one size across every
       frame is the comparison a shopper actually wants.

       Returns null when nothing has been chosen yet. */
    // EVERY DROPDOWN SHOWING A VALUE IS A SELECTION — including Frame: None.
    // Unframed is a real product, not an unanswered question, so it filters
    // like any other value. Only the "Choose a …" placeholder ("") means
    // nothing has been decided, and only that leaves an axis unconstrained.
    //
    // An earlier version filtered on options the shopper had physically
    // touched, which kept more images on screen but meant framed mockups
    // showed while the dropdown read "None" — exactly Joel's item 9. Matching
    // what the controls say beats a fuller strip: the shopper has to be able
    // to trust that what they see is what they're buying.
    function matchedImageIds() {
      if (!optionSelects.length) return null;
      var vals = optionSelects.map(function (s) { return s.value; });
      var ids = {};
      variants.forEach(function (v) {
        var ok = vals.every(function (val, i) { return !val || v.options[i] === val; });
        if (ok && v.featured_image && v.featured_image.id != null) ids[String(v.featured_image.id)] = true;
      });
      return ids;
    }

    /* THE WHOLE RULE, and it is driven by the variant data alone — no product
       type is special-cased, and no count is hardcoded:

         · An image no variant claims is STATIC. It always shows.
         · An image a variant claims shows when it matches the choices made so
           far. Before any choice, every variant matches, so they all show.

       That second line needs no "nothing chosen yet" branch: an unchosen
       dropdown is "", which constrains nothing, so matchedImageIds() returns
       every variant image on its own. The old code had a branch that showed a
       capped sample instead, which became unreachable the moment Frame and
       Color started arriving pre-answered — the cap was dead code describing
       behaviour the page had quietly lost. */
    function applyGallery(wantedIds) {
      if (!thumbs.length) return;
      var vals = optionSelects.map(function (s) { return s.value; });
      thumbs.forEach(function (t) {
        var id = t.dataset.imgId;
        // An image can be claimed two ways: a variant's featured image points
        // at it (Shopify data), or its alt names option values (the pipeline's
        // naming). Every claim that applies must agree with the choices made
        // so far; an image nothing claims is static and always shows.
        var show = true;
        if (variantImageIds[id] && wantedIds) show = !!wantedIds[id];
        var c = altMap[id];
        if (show && c) show = altMatches(c, vals);
        t.hidden = !show;
      });
      // Narrowing changes what the row holds, so start it collapsed again.
      if (thumbsWrap) thumbsWrap.classList.remove('is-expanded');
      syncMoreButton();

      // Hide the strip only when there is genuinely nothing left to show.
      //
      // This used to hide it at fewer than two images, on the theory that a
      // one-image strip is pointless. It reads as the page breaking. Products
      // differ in a way that isn't obvious from the markup: an art print like
      // route-66 has a flat-artwork image that belongs to no variant, so a
      // resolved selection leaves it plus one mockup — two, just past the old
      // threshold. Zion has no such image; all 42 belong to variants, so
      // choosing a size left exactly one and the entire strip vanished.
      // Same code, same template, opposite outcome, decided by product data.
      //
      // One honest thumbnail beats none.
      if (thumbsWrap) {
        var visible = thumbs.filter(function (t) { return !t.hidden; }).length;
        thumbsWrap.hidden = visible === 0;
      }
      syncActiveThumb();
    }

    /* The row shows "Show all N" only when it actually overflows, which is a
       measurement rather than a guess — no threshold to pick, and it stays
       right at any viewport or thumbnail count. */
    function syncMoreButton() {
      if (!moreBtn || !thumbsWrap) return;
      var expanded = thumbsWrap.classList.contains('is-expanded');
      var overflowing = thumbsWrap.scrollWidth > thumbsWrap.clientWidth + 4;
      moreBtn.hidden = !expanded && !overflowing;
      if (moreBtn.hidden) return;
      var visible = thumbs.filter(function (t) { return !t.hidden; }).length;
      moreBtn.textContent = expanded ? 'Show fewer' : 'Show all ' + visible;
      // The name must contain the visible text (WCAG 2.5.3 Label in Name)
      moreBtn.setAttribute('aria-label', (expanded ? 'Show fewer' : 'Show all ' + visible) + ' product images');
      moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    function syncActiveThumb() {
      if (!mainImg) return;
      var id = mainImg.dataset.imgId;
      thumbs.forEach(function (t) { t.classList.toggle('is-active', t.dataset.imgId === id); });
    }

    // srcset travels WITH src. A browser ignores src while a srcset is
    // present, so swapping only src left the stage on the first picture
    // forever once the server rendered a srcset (admin T1, 10-06). The thumb
    // for this id carries the ladder and the file's real shape; anything
    // without one (the frame wood swatches, a fallback URL) drops the srcset
    // so its src is what shows.
    function showImage(src, id) {
      if (!mainImg || !src) return;
      var t = id != null ? pdp.querySelector('[data-adg-thumb][data-img-id="' + id + '"]') : null;
      if (t && t.dataset.srcset) mainImg.srcset = t.dataset.srcset;
      else mainImg.removeAttribute('srcset');
      if (t && t.dataset.w && t.dataset.h) {
        mainImg.setAttribute('width', t.dataset.w);
        mainImg.setAttribute('height', t.dataset.h);
      }
      mainImg.src = src;
      if (id != null) mainImg.dataset.imgId = String(id);
      // carry the thumb's rendered alt onto the stage so the big image never
      // announces a stale variant after a swap
      if (id != null) {
        var altSrc = pdp.querySelector('[data-adg-thumb][data-img-id="' + id + '"] img');
        if (altSrc && altSrc.alt) mainImg.alt = altSrc.alt;
      }
      syncActiveThumb();
    }

    function selectedValues() { return optionSelects.map(function (s) { return s.value; }); }
    function findVariant(vals) {
      return variants.find(function (v) {
        return v.options.length === vals.length && v.options.every(function (o, i) { return o === vals[i]; });
      });
    }
    // A product added normally through the Shopify admin with no options has a
    // single "Default Title" variant, so the section renders no dropdowns and
    // selectedValues() is []. Matching [] against options ["Default Title"]
    // fails, which used to leave the button disabled and reading "Unavailable"
    // on every such product — in stock or not. There is nothing to choose, so
    // the one variant IS the answer.
    function currentVariant() {
      if (!optionSelects.length) return variants.length ? variants[0] : null;
      return findVariant(selectedValues());
    }
    /* Colour only means something once there is a frame to colour, so the
       field appears only then. Frame now has three states, not two:

         ""     nothing decided — colour is hidden and stays unanswered
         "None" unframed, chosen deliberately — colour resolves to None so the
                variant is complete and the shopper can buy
         real   a frame — colour is asked for rather than assumed

       That last case used to silently preselect the first colour. Under a
       model where nothing is answered on the shopper's behalf, it asks. */
    function applyFrameColorRule() {
      if (!frameSelect || !colorField) return;
      var frame = frameSelect.value;
      var framed = frame !== '' && frame !== 'None';
      colorField.hidden = !framed;
      if (!colorSelect) return;

      var placeholder = Array.prototype.find.call(colorSelect.options, function (o) { return o.value === ''; });
      if (frame === 'None') {
        colorSelect.value = 'None';
      } else if (frame === '') {
        if (placeholder) colorSelect.value = '';
      } else if (colorSelect.value === 'None' || colorSelect.value === '') {
        // A framed variant has no "None" colour, so that value can't stand.
        if (placeholder) colorSelect.value = '';
        else {
          var first = Array.prototype.find.call(colorSelect.options, function (o) { return o.value !== 'None'; });
          if (first) colorSelect.value = first.value;
        }
      }
    }
    /* ONLY OFFER COMBINATIONS THAT EXIST.
       Every art print renders Size × Frame × Colour, but only 42 variants are
       real: the four smaller sizes take frames, and 30x40 and 36x48 are
       unframed-only. The dropdowns listed every value regardless, so each
       product offered 18 combinations that cannot be bought — pick 30x40, then
       Standard, and the page just said "Unavailable" without ever hinting the
       frame was the problem (Justin, 2026-08-17).

       An option value is offered when some variant carries it AND agrees with
       everything already chosen to its LEFT. Later options stay open, so the
       shopper is never boxed in by an order they picked things in. Anything
       unreachable is disabled rather than removed, so the list does not
       reshuffle under the cursor, and a selection that has just become
       impossible is cleared back to its placeholder instead of silently
       standing as a lie. */
    function applyAvailability() {
      var chosen = selectedValues();
      optionSelects.forEach(function (sel, i) {
        var reset = false;
        Array.prototype.forEach.call(sel.options, function (opt) {
          if (opt.value === '') return;                 // the "Choose a …" line
          var ok = variants.some(function (v) {
            if (v.options[i] !== opt.value) return false;
            for (var k = 0; k < i; k++) {
              if (chosen[k] && v.options[k] !== chosen[k]) return false;
            }
            return true;
          });
          opt.disabled = !ok;
          if (!ok && sel.value === opt.value) reset = true;
        });
        if (reset) {
          var ph = Array.prototype.find.call(sel.options, function (o) { return o.value === ''; });
          sel.value = ph ? '' : '';
        }
      });
    }
    // `initial` = the first call, on page load. The big image must NOT jump to
    // a variant mockup then: Joel's item 6 asks for the poster / canvas /
    // metal-sign hero shot first, and only then for it to follow the size and
    // frame the shopper picks. So on load we sync price and availability but
    // leave the image the server rendered alone.
    function update(initial) {
      applyAvailability();
      applyFrameColorRule();

      // An option still on its "Choose a …" placeholder means the shopper
      // hasn't decided. Say so plainly instead of quietly picking for them:
      // "From $21.99", a button that names the missing decision, and no
      // variant id in the form so nothing can be added by accident.
      var pending = null;
      for (var i = 0; i < optionSelects.length; i++) {
        if (!optionSelects[i].value) { pending = optionSelects[i]; break; }
      }
      if (pending) {
        if (idInput) idInput.value = '';
        if (priceEl && priceEl.dataset.adgFromPrice) priceEl.textContent = priceEl.dataset.adgFromPrice;
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = pending.dataset.adgChooseLabel || 'Choose an option';
        applyGallery(matchedImageIds());
        return;
      }

      var v = currentVariant();
      if (v) {
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = money(v.price);
        if (addBtn) addBtn.disabled = !v.available;
        if (addLabel) addLabel.textContent = v.available ? 'Add to Cart' : 'Sold Out';
        if (!initial && v.featured_image && mainImg && mainImg.dataset.imgId !== String(v.featured_image.id)) {
          // The thumb for this image already holds the server-made 1200 URL
          // and the srcset ladder, so use it. Building a "_1200x" URL from
          // the variant JSON stays only as the fallback for an image with no
          // thumb (past the 60 cap).
          var vt = pdp.querySelector('[data-adg-thumb][data-img-id="' + v.featured_image.id + '"]');
          showImage(
            vt && vt.dataset.full
              ? vt.dataset.full
              : v.featured_image.src.replace(/(\.(?:jpg|jpeg|png|webp))(\?.*)?$/i, '_1200x$1$2'),
            v.featured_image.id
          );
        } else if (!initial && !v.featured_image && mainImg) {
          // Item 6 on the formats Shopify can't answer for: the big image
          // follows the choice via the same alt mapping the strip uses, but
          // only when exactly one image is left, so it never guesses.
          var vals2 = optionSelects.map(function (s) { return s.value; });
          var hits = thumbs.filter(function (t) {
            var c = altMap[t.dataset.imgId];
            return c && altMatches(c, vals2);
          });
          if (hits.length === 1 && mainImg.dataset.imgId !== hits[0].dataset.imgId) {
            showImage(hits[0].dataset.full, hits[0].dataset.imgId);
          }
        }
      } else {
        // No such variant. This branch used to touch only the button, so the
        // price kept showing the LAST valid variant's figure next to an
        // Unavailable button — a price for something you cannot buy. Clear the
        // id and fall back to the "From $…" line, same as the pending branch.
        if (idInput) idInput.value = '';
        if (priceEl && priceEl.dataset.adgFromPrice) priceEl.textContent = priceEl.dataset.adgFromPrice;
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
      }
      applyGallery(matchedImageIds());
    }
    optionSelects.forEach(function (s) { s.addEventListener('change', function () { update(false); }); });

    if (moreBtn && thumbsWrap) {
      moreBtn.addEventListener('click', function () {
        thumbsWrap.classList.toggle('is-expanded');
        syncMoreButton();
      });
      // A narrower window fits fewer thumbnails, so the row can start or stop
      // overflowing without the selection changing at all.
      window.addEventListener('resize', syncMoreButton);
    }

    // Type switcher → in-place swap (value is the sibling product URL)
    var typeSwitch = pdp.querySelector('[data-adg-type-switch]');
    if (typeSwitch) {
      typeSwitch.addEventListener('change', function () {
        if (typeSwitch.value) swapTo(typeSwitch.value, true);
      });
    }

    // Format tiles → the SAME in-place swap (no reload, no scroll jump).
    // They're real links, so middle-click/new-tab still works natively.
    pdp.querySelectorAll('[data-adg-format-link]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        swapTo(a.getAttribute('href'), true);
      });
    });

    // Quantity stepper
    var qtyInput = pdp.querySelector('[data-adg-qty-input]');
    pdp.querySelectorAll('[data-adg-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var n = (parseInt(qtyInput.value, 10) || 1) + parseInt(btn.dataset.adgQty, 10);
        qtyInput.value = Math.max(1, n);
      });
    });

    // Gallery thumbnails
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        showImage(t.dataset.full, t.dataset.imgId);
      });
    });

    update(true);
  }

  // ---- In-place swap to a sibling product ----
  function isProductUrl(url) { return /\/products\//.test(url); }

  // The document (path + query) the buy box is showing right now. A history
  // move that lands on the same document is only a fragment change — "Skip to
  // content" (#MainContent), any other in-page "#" link, or Back out of one —
  // and must leave the page alone. Without this the popstate handler below
  // reloaded the page and sent keyboard users back to the top (WCAG 2.4.1).
  function docKey(url) {
    try { var u = new URL(url, location.href); return u.pathname + u.search; }
    catch (e) { return String(url).split('#')[0]; }
  }
  var shownKey = docKey(location.href);

  var statusEl = null;
  function announce(msg) {
    if (!statusEl) {
      statusEl = document.createElement('div');
      statusEl.className = 'visually-hidden';
      statusEl.setAttribute('aria-live', 'polite');
      statusEl.setAttribute('data-adg-swap-status', '');
      document.body.appendChild(statusEl);
    }
    statusEl.textContent = '';
    setTimeout(function () { statusEl.textContent = msg; }, 40);
  }

  function swapTo(url, push) {
    var wrap = getWrap();
    if (!wrap || !isProductUrl(url)) { window.location.href = url; return; }
    wrap.style.opacity = '0.45';
    wrap.style.pointerEvents = 'none';
    // Fetch the full product page and extract the buy box. (The Section
    // Rendering API `?sections=` returns empty on this password-protected
    // preview store because the request resolves against the published theme;
    // in production with a published theme we can switch to `?sections=main`
    // for a lighter payload. Full-page fetch is the reliable path here.)
    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newWrap = doc.querySelector(WRAP_SEL);
        if (!newWrap) throw new Error('no wrap in response');
        wrap.innerHTML = newWrap.innerHTML;
        wrap.style.opacity = '';
        wrap.style.pointerEvents = '';
        if (push) history.pushState({ adgSwap: true }, '', url);
        shownKey = docKey(url);
        initPdp(wrap);
        var top = wrap.getBoundingClientRect().top + window.pageYOffset - 90;
        if (window.pageYOffset > top) window.scrollTo(0, top);
        // The page changed under the shopper without a load: say so (WCAG
        // 4.1.3) and put focus on the tile that is now active, where the
        // keyboard user was.
        var title = (doc.querySelector('h1') || {}).textContent || doc.title || '';
        var active = wrap.querySelector('.adg-pdp__format--active');
        var fmtLabel = active && active.querySelector('b') ? active.querySelector('b').textContent : '';
        announce('Now showing ' + title.trim() + (fmtLabel ? ' as ' + fmtLabel.trim() : ''));
        if (active) { active.tabIndex = -1; active.focus({ preventScroll: true }); }
      })
      .catch(function () { window.location.href = url; });   // graceful fallback
  }

  // Back/Forward: only re-swap our own (state-tagged) product entries; for
  // anything else, reload so the real page loads. Same document = a fragment
  // move (see docKey above): nothing to do.
  window.addEventListener('popstate', function (e) {
    if (docKey(location.href) === shownKey) return;
    if (e.state && e.state.adgSwap && isProductUrl(location.href)) {
      swapTo(location.href, false);
    } else if (getWrap()) {
      window.location.reload();
    }
  });

  function boot() {
    var wrap = getWrap();
    if (!wrap) return;
    // Tag the initial product entry so popstate can distinguish it from
    // unrelated history entries (home, listing, etc.).
    history.replaceState({ adgSwap: true }, '', location.href);
    initPdp(wrap);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
